// src/pages/common/RecebimentoPendenteItemsSidePanelContent.tsx
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft } from '@/icons';
import { apiGet } from '../../services/api';
import { OrigemConferencia } from '../../constants';
import ScrollableTable from '../../components/common/ScrollableTable';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import { formatDateTime , formatDate, getEmptyDetailsMessage } from '../../utils/formatUtils';
import CloseButtonSidePanel from '../../components/common/CloseButtonSidePanel';
import Modal from '../../components/common/Modal'; // Import adicionado para modal de histórico
import HistoricoItemModalContent from '../po/HistoricoItemModalContent'; // Import adicionado para conteúdo do modal, assumindo caminho baseado no exemplo anterior
import type { HistoricoResponse } from '../../types/compras/Historico';
import './RecebimentoPendenteItemsSidePanelContent.css'; // CSS específico ou renomeado
import SketchButton from '../../components/common/SketchButton'; // Import adicionado para o botão Rascunho
import { useApiCall } from '../../hooks/useApiCall';
import { showAlert, showConfirm } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';

interface RecebimentoPendenteItemDetail {
  id: number;
  qtdCompra: number;
  qtdCompraExtra: number;
  qtdCompraOriginal: number;
  removeOrdemCompraItem: boolean;
  requisicaoItemId: number;
  ordemCompraId: number;
  codProduto: string;
  nomeProduto: string;
  codCategoriaProduto: number;
  nomeCategoria: string;
  requisicaoId: number;
  nomePessoaUsuario: string;
  prioridade: string;
  nomeFantasiaLoja: string;
  statusRecebimento: string;
  qtdReceber: number;
  qtdRecebidaTotal: number;
  ordemCompra: boolean;
  qtdRecebida: number;
  tipoRequisicao: string;
  contato: string;
  nomeCliente: string;
  nomeConsultor: string;
  origem: OrigemConferencia;
}

interface RecebimentoPendenteDetails {
  dataCriacao: string;
  status: string;
  usuarioId: number;
  nomeUsuario: string;
  codFornecedor: number;
  nomeFornecedor: string;
  lojaEntregaId: number;
  nomeLojaEntrega: string;
  itens: {
    content: RecebimentoPendenteItemDetail[];
    page: {
      size: number;
      number: number;
      totalElements: number;
      totalPages: number;
    };
  };
}

interface RecebimentoPendenteItemsSidePanelContentProps {
  recebimentoPendente: { id: number; status?: string };
  theme: 'dark' | 'light';
  onClose: () => void;
  onSuccessCreateConferencia: (newId: number) => void; // Nome mantido, mas confirme uso consistente no handler de Conferencia.tsx.
}

const RecebimentoPendenteItemsSidePanelContent: React.FC<RecebimentoPendenteItemsSidePanelContentProps> = ({
  recebimentoPendente,
  theme,
  onClose,
  onSuccessCreateConferencia, // Adicionado na desestruturação; motivo: resolve erro de nome não encontrado, permitindo chamada na função handleCreateDraft.
}) => {
  const [header, setHeader] = useState<RecebimentoPendenteDetails | null>(null);

  const fetchItems = useCallback(
    async (
      pageNumber: number,
      _currentItems: RecebimentoPendenteItemDetail[]
    ): Promise<{ items: RecebimentoPendenteItemDetail[]; totalItems: number }> => {
      const queryParams = new URLSearchParams({
        page: pageNumber.toString(),
        size: '10',
      });
      const endpoint = `/v1/recebimento-conferencia/compra-distribuicao/${recebimentoPendente.id}/itens?${queryParams.toString()}`;
      const res = await apiGet(endpoint);
      const details = res.data.content[0]; // Array com um objeto
      if (pageNumber === 0) {
        setHeader(details);
      }
      return { items: details.itens.content, totalItems: details.itens.page.totalElements };
    },
    [recebimentoPendente.id]
  );

  const [isHistoricoModalOpen, setIsHistoricoModalOpen] = useState(false); // Estado adicionado para modal de histórico
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null); // Estado adicionado para ID do item selecionado
  const [historicoData, setHistoricoData] = useState<HistoricoResponse | null>(null); // Estado adicionado para dados do histórico
  const { callApi, isLoading: isCreatingDraft } = useApiCall();

  const handleCreateDraft = async () => {
    if (!(await showConfirm('Deseja iniciar recebimento de todos os itens?'))) {
      return;
    }

    try {
      await callApi('post', `/v1/recebimento-conferencia/${recebimentoPendente.id}`, {}, {
        onSuccess: (newId) => {
          Toast.success(`Conferência ${newId} criada com sucesso.`);
          onSuccessCreateConferencia(newId);
        },
      });
    } catch (error) {
      logger.error('Erro ao criar conferência:', error);
      const backendMsg = axios.isAxiosError<{ error?: string }>(error) ? error.response?.data?.error : undefined;
      await showAlert(backendMsg || 'Erro ao criar conferência. Tente novamente.', { variant: 'error' });
    }
    // Removido finally: gerenciado pelo hook.
  };

  const { items, hasMore, isLoading, loadMore, reset } = usePaginatedFetch<RecebimentoPendenteItemDetail>({
    fetchFunction: fetchItems,
    pageSize: 10,
    uniqueKey: 'id',
    initialPage: 0,
  });

  const handleViewHistoryClick = async (item: RecebimentoPendenteItemDetail) => {
    try {
      const queryParams = new URLSearchParams({
        codProduto: item.codProduto,
        codCategoriaProduto: item.codCategoriaProduto.toString(),
        itemModuloId: item.requisicaoItemId.toString(),
        page: '0',
        size: '10',
      });
      const response = await apiGet(`/v1/historico-itens?${queryParams.toString()}`);
      setHistoricoData(response.data);
      setSelectedItemId(item.requisicaoItemId);
      setIsHistoricoModalOpen(true);
    } catch (error) {
      logger.error('Erro ao carregar histórico:', error);
      if (axios.isAxiosError<{ error?: string }>(error) && error.response?.status === 400 && error.response?.data?.error) {
        await showAlert(error.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao carregar histórico. Tente novamente.', { variant: 'error' });
      }
    }
  }; // Função adicionada para fetch e abertura do modal de histórico, replicando o exemplo

  const handleCloseHistoricoModal = () => {
    setIsHistoricoModalOpen(false);
    setHistoricoData(null);
    setSelectedItemId(null);
  }; // Função adicionada para fechar o modal de histórico

  useEffect(() => {
    reset();
  }, [recebimentoPendente.id, reset]);

  const columns = [
    { header: 'Código', render: (item: RecebimentoPendenteItemDetail) => item.codProduto },
    { header: 'Cód. Categoria', render: (item: RecebimentoPendenteItemDetail) => item.codCategoriaProduto },
    { header: 'Nome do Produto', render: (item: RecebimentoPendenteItemDetail) => item.nomeProduto },
    { header: 'Categoria', render: (item: RecebimentoPendenteItemDetail) => item.nomeCategoria },
    { header: 'Qtd. Total', render: (item: RecebimentoPendenteItemDetail) => item.qtdCompraOriginal },
    { header: 'Requisição', render: (item: RecebimentoPendenteItemDetail) => item.requisicaoId },
    {
      header: 'Prioridade',
      render: (item: RecebimentoPendenteItemDetail) => {
        const prioridade = item.prioridade || 'Não definida';
        if (prioridade === 'URGENTE') {
          return <span className="urgent-priority">{prioridade}</span>;
        }
        return prioridade;
      },
    },
    { header: 'Loja Solicitante', render: (item: RecebimentoPendenteItemDetail) => item.nomeFantasiaLoja },
    { header: 'Requisitante', render: (item: RecebimentoPendenteItemDetail) => item.nomePessoaUsuario },
    { header: 'Status Recebimento', render: (item: RecebimentoPendenteItemDetail) => item.statusRecebimento },
    { header: 'Qtd. Receber', render: (item: RecebimentoPendenteItemDetail) => item.qtdReceber },
    { header: 'Qtd. Total Recebido', render: (item: RecebimentoPendenteItemDetail) => item.qtdRecebidaTotal },
  ];

  return (
    <div className={`recebimento-pendente-items-sidepanel-content ${theme}`}>
      <CloseButtonSidePanel onClick={onClose} className="close-button-absolute" disabled={isLoading || isCreatingDraft}/>
      <div className="title-table">
        <div>
          <div className="facelet-h3">
            <h3>Ordem de Compra Nº {recebimentoPendente.id} | Criada em {header ? formatDateTime(header.dataCriacao) : 'Carregando...'}</h3>
          </div>
            <div className="facelet-p">
            <p>Comprador: {header?.nomeUsuario || 'Carregando...'} | Status: {header?.status || 'Carregando...'}</p>
            <p>Fornecedor: {header?.nomeFornecedor || 'Não informado'} | Loja de entrega: {header?.nomeLojaEntrega}</p>
          </div>
        </div>
      </div>
      {isLoading && items.length === 0 ? (
        <p>Carregando itens...</p>
      ) : (
        <ScrollableTable<RecebimentoPendenteItemDetail>
          data={items}
          hasMore={hasMore}
          loadMore={loadMore}
          isLoading={isLoading}
          columns={columns}
          onSelectChange={() => {}}
          renderHistoryAction={(item) => (
            <button
              className="action-button"
              onClick={() => handleViewHistoryClick(item)}
              title="Ver Histórico do Item"
            >
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </button>
          )}
          fetchDetails={async (item: RecebimentoPendenteItemDetail) => {
            if (!item.tipoRequisicao) return []; // Evita chamada sem tipoRequisicao, retornando vazio.
            const res = await apiGet(`/v1/historico-itens/${item.requisicaoItemId}/po/requisicao-item/detalhes?tipoRequisicao=${item.tipoRequisicao}`);
            return res.data.content;
          }} // Adicionado: fetch para subtabela, replicando exemplo com requisicaoItemId.
            detailsColumns={[
              // Modificado: usa render para condicional baseada em origem nas colunas específicas; motivo: atende à tarefa sem alterar lógica genérica.
              { header: 'Contato', render: (detail: Record<string, unknown>) => detail.origem === 'PLANEJAMENTO' ? "Planejamento" : String(detail.contato ?? '') },
              { header: 'Cliente', render: (detail: Record<string, unknown>) => detail.origem === 'PLANEJAMENTO' ? "Planejamento" : String(detail.nomeCliente ?? '') },
              { header: 'Consultor', render: (detail: Record<string, unknown>) => detail.origem === 'PLANEJAMENTO' ? "Planejamento" : String(detail.nomeConsultor ?? '') },
              { header: 'Cotação', accessor: 'preOrdem' },
              { header: 'Ordem de Compra', accessor: 'ordemCompra' },
              { header: 'Qtd. Compra', accessor: 'qtdCompraOriginal' },
              { header: 'Qtd. Recebida', accessor: 'qtdRecebida' },
              { header: 'Status Recebimento', accessor: 'statusRecebimento' },
              { header: 'Comprador', accessor: 'comprador' },
              { header: 'Fornecedor', accessor: 'nomeFornecedor' },
              { header: 'Previsão Chegada', accessor: 'previsaoChegada', formatter: formatDate },
              { header: 'Conferência', accessor: 'recebimentoConferencia' },
              { header: 'Recebimento', accessor: 'dataConferencia', formatter: formatDateTime },
              { header: 'Conferente', accessor: 'conferente' },
              { header: 'Nº Nota Fiscal', accessor: 'nroNotaFiscal' },
            ]}
          emptyDetailsMessage={(item: RecebimentoPendenteItemDetail) => getEmptyDetailsMessage(item.tipoRequisicao || '')} // usando util centralizada.
          emptyMessage="Nenhum item encontrado."
          showCheckboxes={false}
        />
      )}
      <div className="form-actions">
        <SketchButton
          isLoading={isCreatingDraft}
          onClick={handleCreateDraft}
          disabled={isCreatingDraft}
        /> {/* Botão adicionado: posicionado à direita, com loading, similar a NewReceiptItemsSidePanelContent.tsx. */}
      </div>
      {isHistoricoModalOpen && historicoData && (
        <Modal
          isOpen={isHistoricoModalOpen}
          onClose={handleCloseHistoricoModal}
          theme={theme}
          zIndex={1002}
        >
          <HistoricoItemModalContent
            historico={historicoData}
            requisicaoItemId={selectedItemId!}
            isOpen={isHistoricoModalOpen}
            onClose={handleCloseHistoricoModal}
            theme={theme}
            showCancelButton={false}
          /> {/* Modal adicionado para histórico, replicando o exemplo */}
        </Modal>
      )}
    </div>
  );
};

export default RecebimentoPendenteItemsSidePanelContent;